import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../config/api';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const { getAuthHeaders, user } = useAuth();
  const [status, setStatus] = useState({ plan: 'free', subscriptionStatus: 'trial_active', registrationDate: null, trialEndsAt: null, daysLeft: 7 });
  const [loading, setLoading] = useState(false);
  const uid = user?.uid || null;

  const waitForJwt = async (timeoutMs = 2000) => {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        const jwt = localStorage.getItem('jwtToken');
        if (jwt) return resolve(true);
        if (Date.now() - start >= timeoutMs) return resolve(false);
        setTimeout(tick, 100);
      };
      tick();
    });
  };

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Ensure backend JWT exists to avoid 401s on first load
      await waitForJwt();
      const jwt = localStorage.getItem('jwtToken');
      if (!jwt) {
        // Keep existing status (defaults to trial) and exit
        return;
      }

      const res = await fetch(buildApiUrl('/auth/subscription/status'), { headers: getAuthHeaders() });
      if (!res.ok) {
        // Do not overwrite with invalid payloads
        return;
      }
      const data = await res.json();
      // Validate shape before applying
      if (data && typeof data === 'object' && typeof data.subscriptionStatus === 'string' && typeof data.plan === 'string') {
        setStatus(data);
      }
    } catch (e) {
      console.error('Failed to fetch subscription status', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    const run = async () => {
      await refresh();
      if (cancelled) return;
    };
    run();
    return () => { cancelled = true; };
  }, [uid]);

  // New subscription model: paid when active or cancelled-but-active (access until end)
  const isSubscribed = status?.plan === 'subscribed' && status?.subscriptionStatus === 'subscribed_active';
  const isCancelledButActive = status?.plan === 'subscribed' && status?.subscriptionStatus === 'subscribed_cancelled';
  // Legacy support: treat old pro/premium as subscribed when active
  const isPro = status?.plan === 'pro' || status?.subscriptionStatus === 'pro_active';
  const isPremium = status?.plan === 'premium' || status?.subscriptionStatus === 'premium_active';
  const isPaid = isSubscribed || isPro || isPremium || isCancelledButActive;
  
  const isTrialActive = status?.subscriptionStatus === 'trial_active' && !isPaid;
  const isTrialExpired = (status?.subscriptionStatus === 'trial_expired' || status?.subscriptionStatus === 'subscription_expired') && !isPaid;
  const canWrite = true;
  
  // Check if subscription is expiring soon (within 7 days)
  const isExpiringSoon = useMemo(() => {
    if (!isSubscribed || !status?.subscriptionEndDate) return false;
    const endDate = new Date(status.subscriptionEndDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  }, [isSubscribed, status?.subscriptionEndDate]);
  
  // Can user project beyond 5 years?
  const canProjectBeyond5Years = isPaid;

  const value = useMemo(() => ({
    status,
    loading,
    // New subscription model
    isSubscribed,
    canProjectBeyond5Years,
    // Legacy flags (for backward compatibility)
    isPro,
    isPremium,
    isPaid,
    // Trial status
    isTrialActive,
    isTrialExpired,
    canWrite,
    // Additional subscription info
    billingCycle: status?.billingCycle || null,
    subscriptionEndDate: status?.subscriptionEndDate || null,
    nextBillingDate: status?.nextBillingDate || null,
    isExpiringSoon,
    // Methods
    refresh,
  }), [status, loading, isSubscribed, canProjectBeyond5Years, isPaid, isTrialActive, isTrialExpired, canWrite, isExpiringSoon]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};


